/**
 * Fire-and-forget error capture — the automatic counterpart to the awaited
 * manual {@link ./reportError} path.
 *
 * Every error caught by the React boundary or the global handlers flows
 * through here. It builds the enriched, scrubbed report, applies the
 * dedupe / rate-limit gate, and delivers it without blocking or throwing — a
 * failure to report must never become a second error.
 */

import { generateUuid7 } from "@/utils/uuid7";
import {
  buildReportContent,
  buildFeedbackBody,
  type ErrorSource,
} from "./reportError";
import { fingerprint, shouldReport } from "./dedupe";
import { deliver, enqueue } from "./queue";
import type { ErrorBoundaryScope } from "./ErrorBoundary";

export type CaptureInput = {
  error: Error;
  componentStack?: string | null;
  /** Reuse the boundary's id so the on-screen Reference ID matches the report. */
  eventId?: string;
  scope: ErrorBoundaryScope;
  source: ErrorSource;
};

/** Captures an error and returns the eventId used (generated if not supplied). */
export function captureError(input: CaptureInput): string {
  const eventId = input.eventId || generateUuid7();
  // Vitest runs import real modules whose failure paths call captureError
  // (e.g. the crypto WASM init, which cannot fetch its .wasm under jsdom).
  // Without this gate every CI test run delivers fake crash reports to the
  // real feedback endpoint. Read MODE at call time so tests can stub it.
  if (import.meta.env.MODE === "test") {
    return eventId;
  }
  try {
    // Fingerprint from the raw error and run the dedupe/rate-limit gate BEFORE
    // building the enriched report. Context collection and breadcrumb scrubbing
    // are the expensive work; in a render-loop crash they would otherwise run
    // on every duplicate even though the send is suppressed.
    const fp = fingerprint({
      source: input.source,
      errorName: input.error.name,
      errorMessage: input.error.message,
      stack: input.error.stack,
    });
    if (!shouldReport(fp)) {
      return eventId;
    }
    const content = buildReportContent({ ...input, eventId });
    const body = buildFeedbackBody(content, "");
    void deliver(body)
      .then((ok) => {
        if (!ok) {
          enqueue(body);
        }
      })
      .catch(() => enqueue(body));
  } catch {
    // Capture must be invisible on failure — never re-enter the error path.
  }
  return eventId;
}
